/** ****************** IMPORTS ********************/
import { ClickAwayListener, Grid, makeStyles, Popper } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { amPmOptions, quarterTimesEnd, quarterTimesStart } from '~/src/dash/utils/date';
import {
  borderRadius,
  fontSizing,
  spacingV2,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

/** ****************** INTERFACES ********************/

interface IProps {
  date: moment.Moment | null;
  type?: 'start' | 'end';
  enableClearSelection?: boolean;
  openDirection?: 'up' | 'down';
  disabled?: boolean;
  error?: boolean;

  handleTimeChange: (hour?: number, minute?: number) => void;
  handleAmPmChange: (amPm: string) => void;
}

/** ****************** useStyle ********************/
const useStyles = makeStyles({
  timePicker: {
    height: '100%',
    fontSize: 'inherit',
  },
  timePickerLabel: {
    // height: '20px',
  },
  timePicker_timeInput: {
    width: '52px',
    border: `1px solid ${publicaColors.lightGray}`,
    boxShadow: 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: borderRadius.small,
    marginRight: '8px',
    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
    fontSize: fontSizing.small,
    cursor: 'text',
    display: 'inline-block',
    padding: '6px',
    outline: 'none',
    flexGrow: 1,
  },
  timePicker_ampmInput: {
    width: '64px',
    border: `1px solid ${publicaColors.lightGray}`,
    boxShadow: 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: borderRadius.small,
    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
    fontSize: fontSizing.small,
    cursor: 'text',
    display: 'inline-block',
    padding: '6px',
    outline: 'none',
    flexGrow: 1,
  },
  timePicker_options: {
    width: '100%',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  timePicker_option: {
    height: '30px',
    width: '100%',
    minWidth: '0px',
    fontSize: fontSizing.small,
    color: publicaColors.veryDarkGray,
    fontWeight: 400,
    padding: '6px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'inherit',
  },
  timePicker_optionSelected: {
    fontWeight: 'bold',
    backgroundColor: publicaColors.veryLightGray,
    borderRadius: borderRadius.small,
  },
  timePicker_optionsAmPm: {
    height: '62px',
    textAlign: 'center',
    fontSize: fontSizing.extraSmall,
  },
  timePicker_optionsTime: {
    height: '242px',
  },
  timePicker_inputError: {
    border: '1px solid #e53935',
  },
});

/** ****************** HELPER FUNCTIONS ********************/

const checkHour = (hourString: string): number => {
  const hour = Number(hourString);
  if (isNaN(hour) || hour > 24 || hour < 0) {
    return NaN;
  }
  return hour;
};

const checkMinutes = (minutesString: string): number => {
  const minute = Number(minutesString);
  if (isNaN(minute) || minute > 59 || minute < 0) {
    return NaN;
  }
  return minute;
};

const formatTime = (
  time: string,
  setTime: React.Dispatch<React.SetStateAction<string>>,
  date: moment.Moment | null,
) => {
  if (!time) {
    return;
  }

  let hoursString: string;
  let minutesString: string;

  // If time string is changable to a correct string changes it
  if (time.includes(':')) {
    // Determines if time string conatins colon
    [hoursString, minutesString] = time.split(':');
    if (hoursString.length === 1 && minutesString.length === 2) {
      hoursString = '0' + hoursString;
    } else if (hoursString.length === 2 && minutesString.length === 1) {
      minutesString = minutesString + '0';
    } else if (hoursString.length !== 2 || minutesString.length !== 2) {
      setTime(date ? date.format('hh:mm') : '');
      return;
    }
  } else if (/^\d+$/.test(time)) {
    // Determines if time string is only numbers
    if (time.length === 3) {
      hoursString = '0' + time[0];
      minutesString = time.slice(1);
    } else if (time.length >= 4) {
      hoursString = time.slice(0, 2);
      minutesString = time.slice(2, 4);
    } else {
      setTime(date ? date.format('hh:mm') : '');
      return;
    }
  } else {
    // Reset
    setTime(date ? date.format('hh:mm') : '');
    return;
  }

  // If you get invalid numbers from hour or minutes, resets string
  // If you get valid numbers from hour and minute, set time to new formatted string
  if (isNaN(checkHour(hoursString)) || isNaN(checkMinutes(minutesString))) {
    setTime(date ? date.format('hh:mm') : '');
  } else {
    setTime(hoursString + ':' + minutesString);
  }
};

const formatAmPm = (
  amPm: string,
  setAmPm: React.Dispatch<React.SetStateAction<string>>,
  date: moment.Moment | null,
) => {
  if (!amPm || amPm === 'AM' || amPm === 'PM') {
    return;
  }

  const amPmUpperCase = amPm.toUpperCase();
  if (amPmUpperCase === 'AM' || amPmUpperCase === 'PM') {
    setAmPm(amPmUpperCase);
  } else {
    setAmPm(date ? date.format('A') : '');
  }
};

const getHoursAndMinutes = (time: string, amPm: string): number[] => {
  if (!time || !time.includes(':')) {
    return [NaN, NaN];
  }

  const [hoursString, minutesString] = time.split(':');
  if (hoursString.length !== 2 || minutesString.length !== 2) {
    return [NaN, NaN];
  }

  let hours = checkHour(hoursString);
  const minutes = checkMinutes(minutesString);

  if (isNaN(hours) || isNaN(minutes)) {
    return [NaN, NaN];
  }

  if (amPm === 'PM' && hours < 12) {
    hours += 12;
  } else if (amPm === 'AM' && hours === 12) {
    hours = 0;
  }

  return [hours, minutes];
};

/** ****************** MAIN COMPONENT ********************/
export const TimePickerCustom = (props: IProps) => {
  const { t } = usePublicaTranslation();
  // PROPS
  const {
    date,
    enableClearSelection,
    handleTimeChange,
    handleAmPmChange,
    openDirection,
    type,
    disabled,
    error,
  } = props;

  // quarterTimes based off
  const quarterTimes = type === 'start' ? quarterTimesStart : quarterTimesEnd;

  // CLASSES
  const classes = useStyles();

  // REFS (used to scroll to selected value in the Popper)
  const popperRef = useRef<HTMLDivElement | null>(null);
  const currentTimeRef = useRef<HTMLButtonElement | null>(null);

  /** ******** States **********/
  // Time and AmPm States (Inputs)
  const [time, setTime] = useState<string>(date ? date.format('hh:mm') : '');
  const [amPm, setAmPm] = useState<string>(date ? date.format('A') : '');

  // Anchor States for Popper
  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLDivElement | null>(null);
  const [amPmAnchorEl, setAmPmAnchorEl] = useState<HTMLDivElement | null>(null);

  /** ******** Use Effects **********/
  // Update hours and minutes using time and ampm string
  useEffect(() => {
    const [hours, minutes] = getHoursAndMinutes(time, amPm);
    if (!isNaN(hours) && !isNaN(minutes)) {
      handleTimeChange(hours, minutes);
    }
  }, [time]);

  // Update AmPm time using AmPm string
  useEffect(() => {
    if (amPm === 'AM' || amPm === 'PM') {
      handleAmPmChange(amPm);
    }
  }, [amPm]);

  // If new date comes in and its different than the time/amPm update states
  // If it comes in with a null date but enableClearSelection is on, then clear the time/amPm
  useEffect(() => {
    if (!date) {
      if (enableClearSelection) {
        setTime('');
        setAmPm('');
      }
      return;
    }
    const newTime: string = date.format('hh:mm');
    const newAmPm: string = date.format('A');
    if (time !== newTime) {
      setTime(newTime);
    }
    if (amPm !== newAmPm) {
      setAmPm(newAmPm);
    }
  }, [date]);

  // Scrolls to selected time in Popper
  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (currentTimeRef.current && popperRef.current) {
        popperRef.current.scrollTop = currentTimeRef.current.offsetTop;
      }
    }, 0);

    return () => {
      clearTimeout(scrollTimeout);
    };
  }, [timeAnchorEl]);

  /** ******** POPPER RENDERS **********/
  const renderTimes = () => {
    return (
      <div
        ref={popperRef}
        className={`${classes.timePicker_options} ${classes.timePicker_optionsTime}`}
      >
        {quarterTimes.map((presetTime: string) => (
          <ModelSecondaryButton
            id={presetTime}
            ref={time === presetTime ? currentTimeRef : null}
            className={`${classes.timePicker_option} ${
              presetTime === time ? classes.timePicker_optionSelected : ''
            }`}
            onClick={() => {
              setTime(presetTime);
              setTimeAnchorEl(null);
            }}
          >
            {presetTime}
          </ModelSecondaryButton>
        ))}
      </div>
    );
  };

  const renderAmPm = () => {
    return (
      <div className={`${classes.timePicker_options} ${classes.timePicker_optionsAmPm}`}>
        {amPmOptions.map((presetAmPm: string) => (
          <ModelSecondaryButton
            id={presetAmPm}
            className={`${classes.timePicker_option} ${
              presetAmPm === amPm ? classes.timePicker_optionSelected : ''
            }`}
            onClick={() => {
              setAmPm(presetAmPm);
              setAmPmAnchorEl(null);
            }}
          >
            {presetAmPm}
          </ModelSecondaryButton>
        ))}
      </div>
    );
  };

  /** ******** MAIN RENDER RETURN **********/
  return (
    <Grid item container direction='row' wrap='nowrap' className={classes.timePicker}>
      <ClickAwayListener
        onClickAway={() => {
          if (timeAnchorEl) {
            formatTime(time, setTime, date);
            setTimeAnchorEl(null);
          }
        }}
      >
        <Grid item container direction='column' style={{ margin: 0 }} spacing={1}>
          <label>
            <Text extraSmall muted={disabled} style={{ paddingBottom: spacingV2.extraSmall }}>
              {t('Time')}
            </Text>
          </label>
          <input
            value={time}
            placeholder={t('Time')}
            autoComplete={'off'}
            name='timePickerHour'
            maxLength={5}
            className={`${classes.timePicker_timeInput} ${
              error ? classes.timePicker_inputError : ''
            }`}
            onChange={(e) => setTime(e.currentTarget.value)}
            onClick={(e) => {
              if (!timeAnchorEl) {
                setTimeAnchorEl(e.currentTarget);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                formatTime(time, setTime, date);
                setTimeAnchorEl(null);
              } else {
                if (!timeAnchorEl) {
                  setTimeAnchorEl(e.currentTarget);
                }
              }
            }}
            disabled={!!disabled}
          />
          <Popper
            id={timeAnchorEl ? 'time-popper' : undefined}
            open={!!timeAnchorEl}
            anchorEl={timeAnchorEl}
            placement={openDirection === 'up' ? 'top-start' : 'bottom-start'}
            style={{
              width: '52px',
              border: `1px solid ${publicaColors.lightGray}`,
              borderRadius: borderRadius.small,
              background: publicaColors.white,
              marginTop: '2px',
            }}
            modifiers={[
              // Keeps popper in the specified placement
              {
                name: 'flip',
                enabled: false,
              },
            ]}
          >
            {renderTimes()}
          </Popper>
        </Grid>
      </ClickAwayListener>
      <ClickAwayListener
        onClickAway={() => {
          if (amPmAnchorEl) {
            formatAmPm(amPm, setAmPm, date);
            setAmPmAnchorEl(null);
          }
        }}
      >
        <Grid item container direction='column' style={{ margin: 0 }} spacing={1}>
          <label>
            <Text extraSmall muted={disabled} style={{ paddingBottom: spacingV2.extraSmall }}>
              {t('AMPM')}
            </Text>
          </label>
          <input
            value={amPm}
            placeholder={t('AMPM')}
            autoComplete={'off'}
            name='ampmPickerHour'
            maxLength={2}
            className={`${classes.timePicker_ampmInput} ${
              error ? classes.timePicker_inputError : ''
            }`}
            onChange={(e) => setAmPm(e.currentTarget.value)}
            onClick={(e) => {
              if (!amPmAnchorEl) {
                setAmPmAnchorEl(e.currentTarget);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                formatAmPm(amPm, setAmPm, date);
                setAmPmAnchorEl(null);
              } else {
                if (!amPmAnchorEl) {
                  setAmPmAnchorEl(e.currentTarget);
                }
              }
            }}
            disabled={!!disabled}
          />
          <Popper
            id={amPmAnchorEl ? 'ampm-popper' : undefined}
            open={!!amPmAnchorEl}
            anchorEl={amPmAnchorEl}
            placement={openDirection === 'up' ? 'top-start' : 'bottom-start'}
            style={{
              width: '64px',
              border: `1px solid ${publicaColors.lightGray}`,
              borderRadius: borderRadius.small,
              background: publicaColors.white,
              marginTop: '2px',
            }}
          >
            {renderAmPm()}
          </Popper>
        </Grid>
      </ClickAwayListener>
    </Grid>
  );
};
